import { ENUMS } from './enums';
import { isArrayLength } from './genericHelpers';

const BASE_URL = ENUMS.REACT_APP_STRAPI_BASE_URL;

export const getStrapiImageLink = url => {
  return `${BASE_URL}${url}`;
};

/**
 * Generates an array of image URLs using the base URL and an array of image objects.
 *
 * @param {Array} images - An array of image objects.
 * @param {string} url - The base URL for the images.
 * @returns {Array} An array of image URLs.
 * @throws {TypeError} If the `images` parameter is not an array.
 */
export const getImagesLink = (images, url) => {
  // Generate an array of image URLs using the base URL and the image object's URL attribute.
  const imageUrls =
    isArrayLength(images) &&
    images.map(image => {
      const imageUrl = image?.includes('amazon') ? image : `${url}${image}`;
      return imageUrl;
    });

  // Return the array of image URLs.
  return imageUrls;
};

export const isInfoOnly = l => {
  return !!l?.attributes?.publicData?.lat;
};

export const getBlogData = blog => {
  // Get the base URL from the configuration.
  const baseUrl = process.env.REACT_APP_STRAPI_BASE_URL;

  // Get the blog images and generate image URLs using the base URL.
  const blogImages = blog?.images?.data || [];
  const imagesLink = blogImages?.length && getImagesLink(blogImages, baseUrl);

  // Get the blog title and description.
  const title = blog?.title || '';
  const description = blog?.description || '';
  const tags = blog?.tags || '';
  const createdAt = blog?.createdAt || '';

  // Return an object containing the blog title, description, and the first image URL (if available).
  return { title, description, tags, createdAt, image: imagesLink && imagesLink[0] };
};

/**
 * Safely extract the userType from a user object.
 *
 * @param {Object}  user         — the user object (may be null/undefined)
 * @param {string} [defaultValue=null] — what to return if no userType is found
 * @returns {string|null}
 */
export const getUserType = (user, defaultValue = null) =>
  user?.attributes?.profile?.publicData?.userType ?? defaultValue;

/**
 * Extract the media items from a listing for a given type.
 *
 * @param {Object} listing - The listing object (may be null/undefined).
 * @param {string} type - The type of media items to extract (e.g. 'images', 'videos', 'documents').
 * @returns {Array} An array of media items or an empty array if no media items are found.
 */
export const getListingMedia = (listing = {}, type) => {
  if (!listing?.id?.uuid) return [];
  const items = listing?.attributes?.publicData?.[type];

  // ensure we only ever return an array
  return isArrayLength(items) ? items : [];
};

export const getListingDocuments = listing => {
  if (!listing?.id?.uuid) return {};
  const items = listing?.attributes?.publicData?.documents;
  return items ? items : {};
};

/**
 * @param {{id?:{uuid?:string},attributes?:{privateData?:{isAdminNotificationSent?:boolean}}}|null|undefined} listing
 * @returns {boolean}
 */
export const getAdminNotificationStatus = listing =>
  Boolean(
    listing?.id?.uuid && listing?.attributes?.privateData?.isAdminNotificationSent // has a UUID // and the flag is truthy
  );

export const isListingRejected = listing => {
  if (!listing?.id?.uuid) return false;
  return listing?.attributes?.publicData?.isRejected || false;
};
export const isListingAccepted = listing => {
  if (!listing?.id?.uuid) return false;
  return listing?.attributes?.publicData?.isAccepted || false;
};

export const getSuppliersWhoSentEstimation = listing => {
  if (!listing?.id?.uuid) return null;
  const { metadata } = listing.attributes || {};
  return metadata?.suppliersSentEstimation || [];
};
export const getIsPostEdit = listing => {
  if (!listing?.id?.uuid) return null;
  const { publicData } = listing.attributes || {};
  return publicData?.isPostEditVersion || false;
};
/**
 * Returns the full name of a user by combining first and last names.
 * @param {Object|null|undefined} user - User object with id and profile attributes.
 * @returns {string|null} Full name or null if user is invalid or names are missing.
 */
export const getUserFullName = user => {
  if (!user?.id?.uuid) return null;
  const { firstName = '', lastName = '' } = user.attributes?.profile || {};
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || null;
};
